interface RetryOptions {
  maxRetries?: number;
  initialDelayMs?: number;
  maxDelayMs?: number;
  backoffMultiplier?: number;
  shouldRetry?: (error: any) => boolean;
}

const DEFAULT_OPTIONS: Required<RetryOptions> = {
  maxRetries: 3,
  initialDelayMs: 1000,
  maxDelayMs: 10000,
  backoffMultiplier: 2,
  shouldRetry: (error: any) => {
    // Retry on network errors, timeouts, and 5xx errors
    if (!error) return false;
    
    const errorCode = error?.code?.toUpperCase() || "";
    const errorMessage = error?.message?.toLowerCase() || "";
    const statusCode = error?.status || 0;
    
    // Network errors
    if (errorCode.includes("NETWORK") || errorCode.includes("TIMEOUT")) {
      return true;
    }
    
    // Connection errors
    if (errorMessage.includes("network") || 
        errorMessage.includes("timeout") ||
        errorMessage.includes("connection")) {
      return true;
    }
    
    // Server errors (5xx)
    if (statusCode >= 500 && statusCode < 600) {
      return true;
    }
    
    // Specific Supabase errors that are transient
    if (errorCode === "PGRST301" || // Temporary unavailable
        errorCode === "PGRST504" || // Gateway timeout
        errorCode === "54000") {     // PostgreSQL connection error
      return true;
    }
    
    return false;
  }
};

export async function retryWithBackoff<T>(
  fn: () => Promise<T>,
  options: RetryOptions = {}
): Promise<T> {
  const opts = { ...DEFAULT_OPTIONS, ...options };
  let lastError: any;
  let delay = opts.initialDelayMs;

  for (let attempt = 0; attempt <= opts.maxRetries; attempt++) {
    try {
      return await fn();
    } catch (error) {
      lastError = error;
      
      // Don't retry if we've exhausted attempts
      if (attempt >= opts.maxRetries) {
        throw error;
      }
      
      // Don't retry if the error isn't retryable
      if (!opts.shouldRetry(error)) {
        throw error;
      }
      
      // Log the retry attempt
      console.log(
        `Retry attempt ${attempt + 1}/${opts.maxRetries} after ${delay}ms`,
        error
      );
      
      // Wait before retrying
      await new Promise(resolve => setTimeout(resolve, delay));
      
      // Increase delay for next attempt
      delay = Math.min(delay * opts.backoffMultiplier, opts.maxDelayMs);
    }
  }

  throw lastError;
}
